import { useState } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  FormControlLabel,
  Checkbox,
  TextField,
  InputAdornment,
  IconButton,
  Collapse,
  Badge,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { Dayjs } from "dayjs";
import { HasPermission } from "../../functions/auth";
import { PERMISSIONS } from "../../constants/permissions";
import MultiSelectFilter, { type MultiSelectOption } from "./MultiSelectFilter";
import SearchableMultiSelectFilter from "./SearchableMultiSelectFilter";
import PpAccountMultiSelectFilter from "./PpAccountMultiSelectFilter";
import { filterPanelSx } from "./filterStyles";
import { presets, type PresetDef, type PresetKey } from "./dataFilterPresets";

import { MiniCompany } from "../../types/company";
import { Currency } from "../../pages/Dashboard";
import { Agent } from "../../types/user";

export type StatusOption = {
  value: string;
  label: string;
};

export type PpAccountOption = {
  value: string;
  label: string;
  ppType: string;
};

interface DataFilterProps<K extends string = PresetKey> {
  preset: K;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  onPresetChange: (preset: K) => void;
  // Override the preset dropdown source (e.g. VAMP's full-month presets). Defaults to shared `presets`.
  presetMap?: Record<K, PresetDef>;
  onStartDateChange: (date: Dayjs | null) => void;
  onEndDateChange: (date: Dayjs | null) => void;
  minDate?: Dayjs;
  companies?: MiniCompany[];
  companyId?: number | null;
  onCompanyChange?: (companyId: number | null) => void;
  statusOptions?: StatusOption[];
  status?: string[];
  onStatusChange?: (status: string[]) => void;
  cmStatusOptions?: StatusOption[];
  cmStatus?: string[];
  onCmStatusChange?: (cmStatus: string[]) => void;
  ppTypeOptions?: StatusOption[];
  ppType?: string[];
  onPpTypeChange?: (ppType: string[]) => void;
  companyOptions?: MultiSelectOption[];
  companyIds?: string[];
  onCompanyIdsChange?: (companyIds: string[]) => void;
  skipRdr?: boolean;
  onSkipRdrChange?: (skipRdr: boolean) => void;
  includeUnrepresented?: boolean;
  onIncludeUnrepresentedChange?: (includeUnrepresented: boolean) => void;
  skipShopifyProtected?: boolean;
  onSkipShopifyProtectedChange?: (skipShopifyProtected: boolean) => void;
  autoSubmitted?: boolean;
  onAutoSubmittedChange?: (autoSubmitted: boolean) => void;
  searchQuery?: string;
  onSearchChange?: (search: string) => void;
  searchPlaceholder?: string;
  currency?: Currency;
  onCurrencyChange?: (currency: Currency) => void;
  ppAccountOptions?: PpAccountOption[];
  ppAccounts?: string[];
  onPpAccountsChange?: (ppAccounts: string[]) => void;
  agents?: Agent[];
  selectedAgentIds?: string[];
  onAgentChange?: (agentIds: string[]) => void;
  activeTab?: string;
  onActiveTabChange?: (tab: string) => void;
  tabOptions?: { value: string; label: string }[];
  dateFieldOptions?: { value: string; label: string }[];
  dateField?: string;
  onDateFieldChange?: (dateField: string) => void;
  caseTypeOptions?: { value: string; label: string }[];
  caseType?: string[];
  onCaseTypeChange?: (caseType: string[]) => void;
  reasonCategoryOptions?: { value: string; label: string }[];
  reasonCategory?: string[];
  onReasonCategoryChange?: (reasonCategory: string[]) => void;
  reportingCategoryOptions?: { value: string; label: string }[];
  reportingCategory?: string[];
  onReportingCategoryChange?: (reportingCategory: string[]) => void;
  disputeView?: 'all' | 'assigned';
  onDisputeViewChange?: (value: 'all' | 'assigned') => void;
  processor?: string;
  onProcessorChange?: (value: string) => void;
  processorOptions?: { value: string; label: string }[];
}

const DataFilter = <K extends string = PresetKey>({
  preset,
  startDate,
  endDate,
  onPresetChange,
  presetMap,
  onStartDateChange,
  onEndDateChange,
  minDate,
  companies = [],
  companyId,
  onCompanyChange,
  statusOptions,
  status,
  onStatusChange,
  cmStatusOptions,
  cmStatus,
  onCmStatusChange,
  ppTypeOptions,
  ppType,
  onPpTypeChange,
  companyOptions,
  companyIds,
  onCompanyIdsChange,
  skipRdr,
  onSkipRdrChange,
  includeUnrepresented,
  onIncludeUnrepresentedChange,
  skipShopifyProtected,
  onSkipShopifyProtectedChange,
  autoSubmitted,
  onAutoSubmittedChange,
  searchQuery,
  onSearchChange,
  searchPlaceholder = "Search...",
  currency,
  onCurrencyChange,
  ppAccountOptions,
  ppAccounts,
  onPpAccountsChange,
  agents = [],
  selectedAgentIds = [],
  onAgentChange,
  activeTab,
  onActiveTabChange,
  tabOptions,
  dateFieldOptions,
  dateField,
  onDateFieldChange,
  caseTypeOptions,
  caseType,
  onCaseTypeChange,
  reasonCategoryOptions,
  reasonCategory,
  onReasonCategoryChange,
  reportingCategoryOptions,
  reportingCategory,
  onReportingCategoryChange,
  disputeView,
  onDisputeViewChange,
  processor,
  onProcessorChange,
  processorOptions,
}: DataFilterProps<K>) => {
  const [startPickerOpen, setStartPickerOpen] = useState(false);
  const [endPickerOpen, setEndPickerOpen] = useState(false);

  const showCaseTypeFilter = caseTypeOptions !== undefined && caseTypeOptions.length > 0 && onCaseTypeChange !== undefined;
  const showReasonCategoryFilter = reasonCategoryOptions !== undefined && reasonCategoryOptions.length > 0 && onReasonCategoryChange !== undefined;
  const showReportingCategoryFilter = reportingCategoryOptions !== undefined && reportingCategoryOptions.length > 0 && onReportingCategoryChange !== undefined;

  // Check if any advanced filters are active
  const hasActiveAdvancedFilters = Boolean(skipRdr || skipShopifyProtected || autoSubmitted || (searchQuery && searchQuery.length > 0) || (caseType && caseType.length > 0) || (ppAccounts && ppAccounts.length > 0) || (reasonCategory && reasonCategory.length > 0) || (reportingCategory && reportingCategory.length > 0));
  const [advancedFiltersExpanded, setAdvancedFiltersExpanded] = useState(hasActiveAdvancedFilters);

  // Falls back to the shared presets when a page doesn't inject its own set (e.g. VAMP does).
  const activePresets = (presetMap ?? presets) as unknown as Record<K, PresetDef>;

  const handlePresetChange = (e: SelectChangeEvent<K>) => {
    onPresetChange(e.target.value as K);
  };

  const handleStartChange = (newValue: Dayjs | null) => {
    onStartDateChange(newValue);
  };

  const handleEndChange = (newValue: Dayjs | null) => {
    onEndDateChange(newValue);
  };

  const handleStartInputClick = () => {
    setStartPickerOpen(true);
  };

  const handleEndInputClick = () => {
    setEndPickerOpen(true);
  };

  const handleCompanyChangeInternal = (e: SelectChangeEvent<number | string>) => {
    if (onCompanyChange) {
      const value = e.target.value;
      onCompanyChange(value === "" ? null : (value as number));
    }
  };

  const showCompanyFilter = companies.length > 0 && onCompanyChange !== undefined;
  const showStatusFilter = statusOptions !== undefined && statusOptions.length > 0 && onStatusChange !== undefined;
  const showCmStatusFilter = cmStatusOptions !== undefined && cmStatusOptions.length > 0 && onCmStatusChange !== undefined;
  const showPpTypeFilter = ppTypeOptions !== undefined && ppTypeOptions.length > 0 && onPpTypeChange !== undefined;
  const showCompanyIdsFilter = companyOptions !== undefined && companyOptions.length > 0 && onCompanyIdsChange !== undefined;
  const showPpAccountFilter = ppAccountOptions !== undefined && ppAccountOptions.length > 0 && onPpAccountsChange !== undefined;
  const showAgentFilter = agents.length > 0 && onAgentChange !== undefined;
  const showSkipRdrFilter = skipRdr !== undefined && onSkipRdrChange !== undefined;
  const showIncludeUnrepresentedFilter = includeUnrepresented !== undefined && onIncludeUnrepresentedChange !== undefined;
  const showSkipShopifyProtectedFilter = skipShopifyProtected !== undefined && onSkipShopifyProtectedChange !== undefined;
  const showAutoSubmittedFilter = autoSubmitted !== undefined && onAutoSubmittedChange !== undefined;
  const showSearchFilter = searchQuery !== undefined && onSearchChange !== undefined;
  const showAdvancedFilters = showSkipRdrFilter || showIncludeUnrepresentedFilter || showSkipShopifyProtectedFilter || showAutoSubmittedFilter || showSearchFilter || showCaseTypeFilter || showPpAccountFilter || showReasonCategoryFilter || showReportingCategoryFilter || showCompanyIdsFilter;

  // Count active advanced filters for badge. "only represented companies" is on by
  // default and, when checked, narrows the list by hiding non-represented companies — so
  // count it in that checked state, and only when the filter is actually shown (agency).
  const activeAdvancedFiltersCount = (showIncludeUnrepresentedFilter && !includeUnrepresented ? 1 : 0) + (skipRdr ? 1 : 0) + (skipShopifyProtected ? 1 : 0) + (autoSubmitted ? 1 : 0) + (searchQuery && searchQuery.length > 0 ? 1 : 0) + (caseType && caseType.length > 0 ? 1 : 0) + (ppAccounts && ppAccounts.length > 0 ? 1 : 0) + (reasonCategory && reasonCategory.length > 0 ? 1 : 0) + (reportingCategory && reportingCategory.length > 0 ? 1 : 0) + (companyIds && companyIds.length > 0 ? 1 : 0);

  const toggleAdvancedFilters = () => {
    setAdvancedFiltersExpanded(!advancedFiltersExpanded);
  };

  const handleSkipRdrChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (onSkipRdrChange) {
      onSkipRdrChange(event.target.checked);
    }
  };

  // Displayed as the positive "only represented companies" (checked = only represented =
  // do NOT include unrepresented), so the underlying includeUnrepresented flag is inverted.
  const handleOnlyRepresentedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (onIncludeUnrepresentedChange) {
      onIncludeUnrepresentedChange(!event.target.checked);
    }
  };

  const handleSkipShopifyProtectedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (onSkipShopifyProtectedChange) {
      onSkipShopifyProtectedChange(event.target.checked);
    }
  };

  const handleAutoSubmittedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (onAutoSubmittedChange) {
      onAutoSubmittedChange(event.target.checked);
    }
  };

  const handleCurrencyChange = (e: SelectChangeEvent<Currency>) => {
    const newCurrency = e.target.value as Currency;
    if (onCurrencyChange) {
      onCurrencyChange(newCurrency);
    }
  };

  const agentOptions = [
    { value: "unassigned", label: "Unassigned" },
    { value: "assigned", label: "Assigned" },
    ...agents.map((agent) => ({ value: String(agent.userId), label: `${agent.name} (${agent.email})` })),
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={filterPanelSx}>
        {/* First Row */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="nowrap"
          gap={2}
          sx={{ p: 2, overflowX: 'auto' }}
        >
          <Box display="flex" alignItems="center" flexWrap="nowrap" gap={2} sx={{ flexShrink: 0 }}>
            {/* Date Field Toggle */}
            {dateFieldOptions && dateFieldOptions.length > 0 && onDateFieldChange && (
              <ToggleButtonGroup
                value={dateField}
                exclusive
                onChange={(_, newField) => {
                  if (newField !== null) {
                    onDateFieldChange(newField);
                  }
                }}
                size="small"
                sx={{
                  height: 40,
                  '& .MuiToggleButton-root': {
                    textTransform: 'none',
                    px: 2,
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                    borderColor: 'divider',
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'primary.contrastText',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                    },
                  },
                }}
              >
                {dateFieldOptions.map((option) => (
                  <ToggleButton key={option.value} value={option.value}>
                    {option.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            )}

            {/* Preset Dropdown */}
            <FormControl sx={{ minWidth: 90 }} size="small">
              <InputLabel id="preset-filter-label">Range</InputLabel>
              <Select<K>
                labelId="preset-filter-label"
                id="preset-filter"
                value={preset}
                label="Range"
                onChange={handlePresetChange}
              >
                {(Object.keys(activePresets) as K[]).map((key) => (
                  <MenuItem key={key} value={key}>
                    {activePresets[key].label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Date Pickers */}
            <Box display="flex" alignItems="center" gap={1}>
              <DatePicker
                label="Start"
                format="MM/DD/YYYY"
                value={startDate}
                minDate={minDate}
                onChange={handleStartChange}
                open={startPickerOpen}
                onOpen={() => setStartPickerOpen(true)}
                onClose={() => setStartPickerOpen(false)}
                slotProps={{
                  textField: {
                    size: "small",
                    sx: {
                      width: "fit-content",
                    },
                    onClick: handleStartInputClick,
                  },
                }}
              />
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                —
              </Typography>
              <DatePicker
                label="End"
                format="MM/DD/YYYY"
                value={endDate}
                minDate={minDate}
                onChange={handleEndChange}
                open={endPickerOpen}
                onOpen={() => setEndPickerOpen(true)}
                onClose={() => setEndPickerOpen(false)}
                slotProps={{
                  textField: {
                    size: "small",
                    sx: {
                      width: "fit-content",
                    },
                    onClick: handleEndInputClick,
                  },
                }}
              />
            </Box>

            {/* Tab Toggle */}
            {tabOptions && tabOptions.length > 0 && (
              <ToggleButtonGroup
                value={activeTab}
                exclusive
                onChange={(_, newTab) => {
                  if (newTab !== null && onActiveTabChange) {
                    onActiveTabChange(newTab);
                  }
                }}
                size="small"
                sx={{
                  height: 40,
                  '& .MuiToggleButton-root': {
                    textTransform: 'none',
                    px: 2.5,
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    borderColor: 'divider',
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'primary.contrastText',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                    },
                  },
                }}
              >
                {tabOptions.map((option) => (
                  <ToggleButton key={option.value} value={option.value}>
                    {option.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            )}
          </Box>

          {/* Right side filters */}
          <Box display="flex" alignItems="center" flexWrap="nowrap" gap={2} sx={{ flexShrink: 0 }}>

            {/* Processor Filter */}
            {processorOptions && processorOptions.length > 0 && (
              <FormControl sx={{ minWidth: 140 }} size="small">
                <InputLabel id="processor-filter-label" shrink>Processor</InputLabel>
                <Select
                  labelId="processor-filter-label"
                  id="processor-filter"
                  value={processor ?? ""}
                  label="Processor"
                  displayEmpty
                  notched
                  onChange={(e) => onProcessorChange?.(e.target.value)}
                  renderValue={(val) =>
                    processorOptions.find((o) => o.value === val)?.label ??
                    processorOptions[0]?.label ??
                    ""
                  }
                >
                  {processorOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Status Filter */}
            {showStatusFilter && (
              <MultiSelectFilter
                label="PP Status"
                options={statusOptions}
                value={status ?? []}
                onChange={onStatusChange!}
                width={170}
              />
            )}

            {/* CM Status Filter */}
            {showCmStatusFilter && (
              <MultiSelectFilter
                label="Status"
                options={cmStatusOptions!}
                value={cmStatus ?? []}
                onChange={onCmStatusChange!}
                width={170}
              />
            )}

            {/* PP Type Filter */}
            {showPpTypeFilter && (
              <MultiSelectFilter
                label="PP Type"
                options={ppTypeOptions!}
                value={ppType ?? []}
                onChange={onPpTypeChange!}
                width={150}
              />
            )}

            {/* Agent Filter */}
            {showAgentFilter && (
              <HasPermission permission={PERMISSIONS.DISPUTES_ALL_COMPANIES}>
                <SearchableMultiSelectFilter
                  label="Assigned To"
                  options={agentOptions}
                  value={selectedAgentIds}
                  onChange={onAgentChange!}
                  width={170}
                />
              </HasPermission>
            )}

            {/* Company Filter */}
            {showCompanyFilter && (
              <HasPermission permission={PERMISSIONS.DISPUTES_ALL_COMPANIES}>
                <FormControl sx={{ minWidth: 200 }} size="small">
                  <InputLabel id="company-filter-label">Company</InputLabel>
                  <Select
                    labelId="company-filter-label"
                    id="company-filter"
                    value={companyId || ""}
                    label="Company"
                    onChange={handleCompanyChangeInternal}
                  >
                    <MenuItem value="">All</MenuItem>
                    {companies.map((company) => (
                      <MenuItem key={company.id} value={company.id}>
                        {company.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </HasPermission>
            )}

            {/* Currency Filter */}
            {currency && (
              <FormControl sx={{ minWidth: 100 }} size="small">
                <InputLabel id="currency-filter-label">Currency</InputLabel>
                <Select
                  labelId="currency-filter-label"
                  id="currency-filter"
                  value={currency}
                  label="Currency"
                  onChange={handleCurrencyChange}
                >
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="EUR">EUR</MenuItem>
                </Select>
              </FormControl>
            )}

            {/* Dispute View Filter (All disputes vs Assigned only) — admins only */}
            {disputeView !== undefined && onDisputeViewChange && (
              <HasPermission permission={PERMISSIONS.COMPANIES_READ_ALL}>
                <FormControl sx={{ minWidth: 160 }} size="small">
                  <InputLabel id="dispute-view-label">Show</InputLabel>
                  <Select
                    labelId="dispute-view-label"
                    id="dispute-view"
                    value={disputeView}
                    label="Show"
                    onChange={(e: SelectChangeEvent<'all' | 'assigned'>) => onDisputeViewChange(e.target.value as 'all' | 'assigned')}
                    renderValue={(val) => (val === 'assigned' ? 'Assigned only' : 'All disputes')}
                  >
                    <MenuItem value="all" sx={{ alignItems: "flex-start", py: 1 }}>
                      <ListItemText
                        primary="All disputes"
                        secondary="Every dispute in the period"
                        secondaryTypographyProps={{ fontSize: "0.75rem" }}
                      />
                    </MenuItem>
                    <MenuItem value="assigned" sx={{ alignItems: "flex-start", py: 1 }}>
                      <ListItemText
                        primary="Assigned only"
                        secondary="Only disputes assigned to an agent"
                        secondaryTypographyProps={{ fontSize: "0.75rem" }}
                      />
                    </MenuItem>
                  </Select>
                </FormControl>
              </HasPermission>
            )}

            {/* Advanced Filters Toggle */}
            {showAdvancedFilters && (
              <IconButton
                onClick={toggleAdvancedFilters}
                aria-label="Toggle advanced filters"
                size="small"
                sx={{
                  border: '1px solid',
                  borderColor: advancedFiltersExpanded ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  backgroundColor: advancedFiltersExpanded ? 'primary.50' : 'transparent',
                  '&:hover': {
                    backgroundColor: advancedFiltersExpanded ? 'primary.100' : 'action.hover',
                  },
                }}
              >
                <Badge
                  badgeContent={activeAdvancedFiltersCount}
                  color="primary"
                  sx={{
                    '& .MuiBadge-badge': {
                      fontSize: 10,
                      height: 16,
                      minWidth: 16,
                    },
                  }}
                >
                  <FilterListIcon
                    fontSize="small"
                    sx={{ color: advancedFiltersExpanded ? 'primary.main' : 'text.secondary' }}
                  />
                </Badge>
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Second Row - Advanced Filters */}
        {showAdvancedFilters && (
          <Collapse in={advancedFiltersExpanded}>
            <Box
              display="flex"
              alignItems="center"
              flexWrap="wrap"
              gap={2}
              sx={{
                px: 2,
                pb: 2,
                pt: 1.5,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              {/* Company Multi-Select Filter */}
              {showCompanyIdsFilter && (
                <SearchableMultiSelectFilter
                  label="Company"
                  options={companyOptions!}
                  value={companyIds ?? []}
                  onChange={onCompanyIdsChange!}
                  width={200}
                  searchPlaceholder="Search companies…"
                  showSelectAllClearAll
                />
              )}

              {/* Search Filter */}
              {showSearchFilter && (
                <TextField
                  size="small"
                  placeholder={searchPlaceholder}
                  value={searchQuery || ""}
                  onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: 300,
                  }}
                />
              )}

              {/* PP Account Filter */}
              {showPpAccountFilter && (
                <PpAccountMultiSelectFilter
                  label="PP Account"
                  options={ppAccountOptions!}
                  value={ppAccounts ?? []}
                  onChange={onPpAccountsChange!}
                  width={200}
                />
              )}

              {/* Case Type Filter */}
              {showCaseTypeFilter && (
                <MultiSelectFilter
                  label="Case Type"
                  options={caseTypeOptions!}
                  value={caseType ?? []}
                  onChange={onCaseTypeChange!}
                  width={170}
                />
              )}

              {/* Reason Category Filter */}
              {showReasonCategoryFilter && (
                <MultiSelectFilter
                  label="Dispute Reason"
                  options={reasonCategoryOptions!}
                  value={reasonCategory ?? []}
                  onChange={onReasonCategoryChange!}
                  width={170}
                />
              )}

              {/* PayPal Reporting Category Filter */}
              {showReportingCategoryFilter && (
                <MultiSelectFilter
                  label="PayPal Category"
                  options={reportingCategoryOptions!}
                  value={reportingCategory ?? []}
                  onChange={onReportingCategoryChange!}
                  width={180}
                />
              )}

              {/* Skip RDR Filter */}
              {showSkipRdrFilter && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={skipRdr}
                      onChange={handleSkipRdrChange}
                      size="small"
                    />
                  }
                  label="hide RDRs"
                  sx={{ mr: 2 }}
                />
              )}

              {/* Only Represented Companies Filter */}
              {showIncludeUnrepresentedFilter && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!includeUnrepresented}
                      onChange={handleOnlyRepresentedChange}
                      size="small"
                    />
                  }
                  label="only represented companies"
                  sx={{ mr: 2 }}
                />
              )}

              {/* Skip Shopify Protected Filter */}
              {showSkipShopifyProtectedFilter && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={skipShopifyProtected}
                      onChange={handleSkipShopifyProtectedChange}
                      size="small"
                    />
                  }
                  label="hide Shopify Protected"
                  sx={{ mr: 2 }}
                />
              )}

              {/* Auto-submitted (Shopify) Filter */}
              {showAutoSubmittedFilter && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={autoSubmitted}
                      onChange={handleAutoSubmittedChange}
                      size="small"
                    />
                  }
                  label="Auto-submitted (Shopify)"
                />
              )}
            </Box>
          </Collapse>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default DataFilter;

